/**
 * ActivityUploadModal - Upload work (site photos & comments)
 * Matches iOS PMUploadWorkSheet flow: team, photos, optional comment, optional voice note.
 */

import React, { useState, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/firebase';
import VoiceRecorder from './VoiceRecorder';

/** Same policy as native apps + Cloud Functions (workUploadVideoPolicy.js) */
const WORK_VIDEO_MAX_CLIPS = 3;
const WORK_VIDEO_MAX_PER_CLIP_SEC = 60;
const WORK_VIDEO_MAX_TOTAL_SEC = 60;

const ActivityUploadModal = ({ open, onClose, onSuccess, projectId, teams = [], isProjectFinished = false }) => {
  const [comment, setComment] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState('no_team');
  const [submitting, setSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [voiceNoteFile, setVoiceNoteFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const getVideoDurationSeconds = (file) =>
    new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.onloadedmetadata = () => {
        const d = v.duration;
        URL.revokeObjectURL(url);
        resolve(Number.isFinite(d) ? d : 0);
      };
      v.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Could not read video'));
      };
      v.src = url;
    });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const imageFiles = attachedFiles.filter((f) => f.type.startsWith('image/'));
    const videoFiles = attachedFiles.filter((f) => f.type.startsWith('video/'));
    if (imageFiles.length === 0 && videoFiles.length === 0) {
      alert('Please add at least one photo or video');
      return;
    }
    if (videoFiles.length > WORK_VIDEO_MAX_CLIPS) {
      alert(`You can attach at most ${WORK_VIDEO_MAX_CLIPS} video clips.`);
      return;
    }

    const userId = auth.currentUser?.uid;
    if (!userId) {
      alert('You must be signed in to post activity');
      return;
    }

    if (isProjectFinished) {
      alert('This project is finished. Activity posts are no longer available.');
      return;
    }

    setSubmitting(true);
    setUploadingFiles(true);

    try {
      const storage = getStorage();
      const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

      let totalVideoSec = 0;
      for (let v = 0; v < videoFiles.length; v++) {
        const dur = await getVideoDurationSeconds(videoFiles[v]);
        if (dur > WORK_VIDEO_MAX_PER_CLIP_SEC) {
          alert(`Each video must be ${WORK_VIDEO_MAX_PER_CLIP_SEC} seconds or shorter.`);
          setSubmitting(false);
          setUploadingFiles(false);
          return;
        }
        totalVideoSec += dur;
      }
      if (totalVideoSec > WORK_VIDEO_MAX_TOTAL_SEC) {
        alert('Total video must be 1 minute or less (all clips combined).');
        setSubmitting(false);
        setUploadingFiles(false);
        return;
      }

      const imageUrls = [];
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        const storageRef = ref(storage, `projects/${projectId}/work_uploads/${userId}/${uploadId}_${i}.jpg`);
        await uploadBytes(storageRef, file, { contentType: file.type || 'image/jpeg' });
        const url = await getDownloadURL(storageRef);
        imageUrls.push(url);
      }

      const videoUrls = [];
      for (let j = 0; j < videoFiles.length; j++) {
        const file = videoFiles[j];
        const ext = (file.name.split('.').pop() || 'mp4').replace(/[^a-z0-9]/gi, '') || 'mp4';
        const storageRef = ref(storage, `projects/${projectId}/work_uploads/${userId}/${uploadId}_video_${j}.${ext}`);
        await uploadBytes(storageRef, file, { contentType: file.type || 'video/mp4' });
        const url = await getDownloadURL(storageRef);
        videoUrls.push(url);
      }

      // Upload voice note if present
      let voiceNoteUrl = null;
      if (voiceNoteFile) {
        const ext = voiceNoteFile.name.split('.').pop() || 'webm';
        const voiceRef = ref(storage, `projects/${projectId}/work_uploads/${userId}/voice_notes/${uploadId}.${ext}`);
        await uploadBytes(voiceRef, voiceNoteFile);
        voiceNoteUrl = await getDownloadURL(voiceRef);
      }

      setUploadingFiles(false);

      // Resolve team display name
      const teamId = selectedTeam === 'no_team' ? '' : selectedTeam;
      const teamName = selectedTeam === 'no_team' ? 'No Team' : (teams.find(t => t.id === selectedTeam)?.name || 'No Team');

      // Write to Firestore - projects/{projectId}/work_uploads/{uploadId}
      const workUpload = {
        id: uploadId,
        userId,
        projectId,
        imageUrls,
        timestamp: new Date(),
        comment: comment.trim(),
        teamId,
        teamName,
      };
      if (videoUrls.length > 0) workUpload.videoUrls = videoUrls;
      if (voiceNoteUrl) workUpload.voiceNoteUrl = voiceNoteUrl;

      await setDoc(doc(db, `projects/${projectId}/work_uploads/${uploadId}`), {
        ...workUpload,
        timestamp: workUpload.timestamp,
      });

      // Reset and close
      setComment('');
      setAttachedFiles([]);
      setSelectedTeam('no_team');
      setVoiceNoteFile(null);
      onSuccess?.();
      onClose();
    } catch (err) {
      console.error('Error posting activity:', err);
      alert('Failed to post activity. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadingFiles(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setComment('');
      setAttachedFiles([]);
      setSelectedTeam('no_team');
      setVoiceNoteFile(null);
      onClose();
    }
  };

  const addImageFiles = (files) => {
    const validFiles = Array.from(files || []).filter(
      (f) => f.type.startsWith('image/') || f.type.startsWith('video/')
    );
    setAttachedFiles((prev) => {
      let videoCount = prev.filter((f) => f.type.startsWith('video/')).length;
      const toAdd = [];
      let skippedVideos = 0;
      for (const f of validFiles) {
        if (f.type.startsWith('video/')) {
          if (videoCount >= WORK_VIDEO_MAX_CLIPS) {
            skippedVideos += 1;
            continue;
          }
          videoCount += 1;
        }
        toAdd.push(f);
      }
      if (skippedVideos > 0) {
        alert(`You can attach at most ${WORK_VIDEO_MAX_CLIPS} video clips.`);
      }
      return [...prev, ...toAdd];
    });
  };

  const handleFileSelect = (e) => {
    addImageFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!submitting) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (submitting) return;
    addImageFiles(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Object URLs for image previews - cleanup on unmount
  const [previewUrls, setPreviewUrls] = useState([]);
  useEffect(() => {
    const urls = attachedFiles.map(f => URL.createObjectURL(f));
    setPreviewUrls(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [attachedFiles]);

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.35)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: 14,
        padding: 0,
        minWidth: 500,
        maxWidth: '90vw',
        maxHeight: '90vh',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 28px',
          borderBottom: '1px solid var(--branding-app-surface)',
        }}>
          <span style={{ fontWeight: 600, fontSize: 18, color: 'var(--cc-text-primary)' }}>
            Upload work
          </span>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            style={{
              background: 'var(--cc-cancel-bg)',
              color: 'var(--cc-text-primary)',
              border: 'none',
              borderRadius: 6,
              padding: '4px 14px',
              fontWeight: 500,
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: 16,
            }}
          >
            Close
          </button>
        </div>

        <div style={{ padding: 28, overflow: 'auto', maxHeight: 'calc(90vh - 100px)' }}>
          <form onSubmit={handleSubmit} style={{ maxWidth: 600, margin: 0 }}>
            {/* Team Selection */}
            {teams.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontWeight: 500,
                  color: 'var(--cc-text-primary)',
                  marginBottom: 6,
                  fontSize: 14,
                }}>
                  Upload for team
                </label>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 14,
                    backgroundColor: submitting ? '#f9fafb' : '#fff',
                    boxSizing: 'border-box',
                  }}
                >
                  <option value="no_team">No Team (General Upload)</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Photos */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14,
              }}>
                Photos *
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${isDragOver ? 'var(--branding-app-primary)' : '#d1d5db'}`,
                  borderRadius: 6,
                  padding: 20,
                  textAlign: 'center',
                  backgroundColor: isDragOver ? '#eff6ff' : '#fafafa',
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}
              >
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  disabled={submitting}
                  style={{ display: 'none' }}
                  id="activity-file-input"
                />
                <label
                  htmlFor="activity-file-input"
                  style={{
                    display: 'inline-block',
                    background: 'var(--branding-app-primary)',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: 6,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  Choose files
                </label>
                <p style={{ margin: '8px 0 0 0', fontSize: 12, color: 'var(--cc-text-muted)' }}>
                  Drag &amp; drop or choose images or video. Video: up to {WORK_VIDEO_MAX_CLIPS} clips, 1 minute total, {WORK_VIDEO_MAX_PER_CLIP_SEC}s max per clip.
                </p>
              </div>

              {attachedFiles.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {attachedFiles.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          flexShrink: 0,
                        }}
                      >
                        {previewUrls[index] ? (
                          attachedFiles[index]?.type?.startsWith('video/') ? (
                            <video
                              src={previewUrls[index]}
                              muted
                              playsInline
                              style={{
                                width: 64,
                                height: 64,
                                objectFit: 'cover',
                                borderRadius: 6,
                                border: '1px solid #e2e8f0',
                              }}
                            />
                          ) : (
                          <img
                            src={previewUrls[index]}
                            alt=""
                            style={{
                              width: 64,
                              height: 64,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid #e2e8f0',
                            }}
                          />
                          )
                        ) : (
                          <div
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: 6,
                              border: '1px solid #e2e8f0',
                              background: 'var(--branding-app-surface)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 24,
                              color: 'var(--branding-app-surface-panel-hint)',
                            }}
                          >
                            🖼️
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          disabled={submitting}
                          style={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            lineHeight: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Comment */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14,
              }}>
                Comment (optional)
              </label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                disabled={submitting}
                rows={3}
                placeholder="Add a comment..."
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14,
                  resize: 'vertical',
                  backgroundColor: submitting ? '#f9fafb' : '#fff',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Voice Note */}
            <div style={{ marginBottom: 24 }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14,
              }}>
                Voice note (optional)
              </label>
              <VoiceRecorder
                onVoiceRecorded={(file) => setVoiceNoteFile(file)}
                disabled={submitting}
              />
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                style={{
                  background: 'var(--branding-app-surface)',
                  color: 'var(--cc-text-secondary)',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  padding: '10px 20px',
                  fontWeight: 500,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || attachedFiles.length === 0}
                style={{
                  background: submitting || attachedFiles.length === 0 ? '#9ca3af' : 'var(--branding-app-primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '10px 20px',
                  fontWeight: 600,
                  cursor: submitting || attachedFiles.length === 0 ? 'not-allowed' : 'pointer',
                  fontSize: 14,
                }}
              >
                {submitting ? (uploadingFiles ? 'Uploading...' : 'Uploading...') : 'Upload work'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ActivityUploadModal;
